import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CustomerInformation } from "./components/CustomerInformation";
import { PaymentMethodSelection } from "./components/PaymentMethodSelection";
import { OrderSummary } from "./components/OrderSummary";
import { CheckoutProvider, useCheckoutContext } from "./CheckoutContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ArrowLeft, Info } from "lucide-react";
import { PaymentAdapterRenderer } from "./payments/adapters/PaymentAdapterRenderer";
import { parsePaymentSelectionKey } from "@/services/student/checkout/checkout.interfaces";
import { useOrderPaymentStatus } from "./payments/hooks/useOrderPaymentStatus";
import { recordUserBehaviorEvent } from "@/services/analytics/userBehaviorEvents.api";
import { CheckoutWizardRail } from "./components/CheckoutWizardRail";
import { CheckoutMobileProgress } from "./components/CheckoutMobileProgress";
import { CheckoutActionBar } from "./components/CheckoutActionBar";
import { CheckoutPackageStep } from "./components/CheckoutPackageStep";
import { CheckoutWizardStepId } from "./components/checkoutWizard.constants";
import { CheckoutPaymentMobileRecap } from "./components/CheckoutPaymentMobileRecap";
import { CheckoutStepIntro } from "./components/CheckoutStepIntro";
import { PromotionalCode } from "./components/PromotionalCode";
import { CheckoutPaymentMethodRecap } from "./components/CheckoutPaymentMethodRecap";
import {
  getPaymentInstrumentIntro,
  getPaymentFailedMessage,
  PAYMENT_AWAITING_HINT,
  PAYMENT_RETRY_BUTTON_LABEL,
  getPaymentPlaceholderHint,
  CHECKOUT_PRIMARY_CTA_CLASS,
  CHECKOUT_CONTINUE_LABEL,
  CHECKOUT_CONFIRM_PAYMENT_LABEL,
  CHECKOUT_PROCESSING_PAYMENT_LABEL,
  CHECKOUT_COMPLETED_LABEL,
  PAYMENT_AWAITING_NAV_LABEL,
} from "./components/checkoutPaymentLabels";
import { PaymentProcessingOverlay } from "./components/PaymentProcessingOverlay";

const CheckoutPageContent: React.FC = () => {
  const navigate = useNavigate();
  const {
    state,
    packageInfo,
    setPaymentMethod,
    getFinalPrice,
    formatCurrency,
    completePaymentFlow,
    resetPaymentAttempt,
    processCheckout,
    validateCustomerInfo,
    canProcessCheckout,
  } = useCheckoutContext();

  const [currentStep, setCurrentStep] = React.useState<CheckoutWizardStepId>(0);
  const [maxReachableStep, setMaxReachableStep] = React.useState<CheckoutWizardStepId>(0);
  const [overlayPhase, setOverlayPhase] = React.useState<"processing" | "success">(
    "processing"
  );
  // Tracks whether the user has actually submitted the Mercado Pago card Brick.
  // The processing overlay must mount only after the Brick submit — mounting it
  // right after the order commit would intercept pointer events and block the
  // Brick (full-screen modal with aria-modal=true). See design Decision 1.
  const [cardPaymentSubmitted, setCardPaymentSubmitted] = React.useState(false);

  const stepHeadingRefs = React.useRef<Array<HTMLHeadingElement | null>>([null, null, null]);
  const celebrationNavTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>();

  const isAwaitingPayment = state.paymentPhase === "awaiting_payment";
  const isInstrumentStage = isAwaitingPayment && Boolean(state.paymentSession);
  const paymentSelection = state.selectedPaymentKey
    ? parsePaymentSelectionKey(state.selectedPaymentKey)
    : null;
  const isCardMethod = paymentSelection?.method === "Card";

  const navigateToSuccess = React.useCallback(() => {
    navigate("/student/checkout/success", {
      state: {
        orderId: state.orderId,
        packageId: packageInfo?.id,
        packageName: packageInfo?.name,
      },
    });
  }, [navigate, state.orderId, packageInfo?.id, packageInfo?.name]);

  const handleCardSubmit = React.useCallback(() => {
    setCardPaymentSubmitted(true);
  }, []);

  const handlePaymentConfirmed = React.useCallback(() => {
    setOverlayPhase("success");
    if (packageInfo && state.selectedPaymentKey) {
      recordUserBehaviorEvent("PurchaseCompleted", {
        packageId: packageInfo.id,
        paymentMethod: state.selectedPaymentKey,
        voucherApplied: String(state.promoCode.applied),
      });
    }
    // Defer completePaymentFlow until navigate so paymentPhase stays
    // awaiting_payment and the celebration overlay remains mounted.
    // Spec order: analytics → completePaymentFlow → navigate; completePaymentFlow
    // runs in the timeout callback immediately before navigateToSuccess.
    if (celebrationNavTimeoutRef.current) {
      clearTimeout(celebrationNavTimeoutRef.current);
    }
    celebrationNavTimeoutRef.current = setTimeout(() => {
      completePaymentFlow();
      navigateToSuccess();
    }, 1500);
  }, [
    packageInfo,
    state.selectedPaymentKey,
    state.promoCode.applied,
    completePaymentFlow,
    navigateToSuccess,
  ]);

  const { timedOut, refetch } = useOrderPaymentStatus({
    orderId: state.orderId,
    enabled: isAwaitingPayment,
    onPaid: handlePaymentConfirmed,
    onFailed: () => {
      setCardPaymentSubmitted(false);
      resetPaymentAttempt(getPaymentFailedMessage(state.selectedPaymentKey));
    },
    onTimeout: () => {
      // timeout message + retry button shown in UI below
    },
  });

  React.useEffect(() => {
    return () => {
      if (celebrationNavTimeoutRef.current) {
        clearTimeout(celebrationNavTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!timedOut) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.getElementById("checkout-awaiting-confirmation")?.scrollIntoView({
      block: "nearest",
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, [timedOut]);

  React.useEffect(() => {
    if (isAwaitingPayment) {
      setOverlayPhase("processing");
    }
  }, [isAwaitingPayment]);

  React.useEffect(() => {
    if (isAwaitingPayment && currentStep < 2) {
      setCurrentStep(2);
      setMaxReachableStep(2);
    }
  }, [isAwaitingPayment, currentStep]);

  React.useEffect(() => {
    const headingEl = stepHeadingRefs.current[currentStep];
    if (headingEl) {
      headingEl.focus({ preventScroll: false });
    }
  }, [currentStep]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isAwaitingPayment || state.isCommittingOrder) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      if (e.key === "ArrowLeft" && currentStep > 0) {
        e.preventDefault();
        goBack();
      } else if (e.key === "ArrowRight" && currentStep < maxReachableStep && currentStep < 2) {
        e.preventDefault();
        setCurrentStep((s) => (s + 1) as CheckoutWizardStepId);
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentStep, maxReachableStep, isAwaitingPayment, state.isCommittingOrder]);

  const goToStep = (step: CheckoutWizardStepId) => {
    if (step <= maxReachableStep && !isAwaitingPayment) {
      setCurrentStep(step);
    }
  };

  const goBack = () => {
    if (currentStep > 0 && !isAwaitingPayment) {
      setCurrentStep((s) => (s - 1) as CheckoutWizardStepId);
    }
  };

  const advanceFromPackage = () => {
    setCurrentStep(1);
    setMaxReachableStep((prev) => Math.max(prev, 1) as CheckoutWizardStepId);
  };

  const advanceFromCustomer = async () => {
    const valid = await validateCustomerInfo();
    if (!valid) return;
    setCurrentStep(2);
    setMaxReachableStep(2);
  };

  const handleConfirmOrder = async () => {
    const result = await processCheckout();
    if (result.status === "completed") {
      navigateToSuccess();
    }
  };

  const handleChangePaymentMethod = () => {
    setCardPaymentSubmitted(false);
    resetPaymentAttempt();
  };

  const handlePrimaryAction = async () => {
    if (isAwaitingPayment || state.isCommittingOrder || state.paymentPhase === "committing") {
      return;
    }
    if (currentStep === 0) {
      advanceFromPackage();
    } else if (currentStep === 1) {
      await advanceFromCustomer();
    } else {
      await handleConfirmOrder();
    }
  };

  const isCommitting = state.isCommittingOrder || state.paymentPhase === "committing";

  const primaryLabel = (() => {
    if (currentStep !== 2) return CHECKOUT_CONTINUE_LABEL;
    if (isCommitting) return CHECKOUT_PROCESSING_PAYMENT_LABEL;
    if (isAwaitingPayment) return PAYMENT_AWAITING_NAV_LABEL;
    if (state.paymentPhase === "completed") return CHECKOUT_COMPLETED_LABEL;
    return CHECKOUT_CONFIRM_PAYMENT_LABEL;
  })();

  const primaryDisabled =
    currentStep === 2
      ? isCommitting ||
        state.isApplyingPromoCode ||
        state.isApplyingVoucher ||
        isAwaitingPayment ||
        state.paymentPhase === "completed" ||
        state.isPackageAlreadyPurchased ||
        state.isPackageUnavailableForPurchase ||
        !canProcessCheckout()
      : false;

  const showActionBack = currentStep > 0 && !isAwaitingPayment && !isCommitting;

  return (
    <div className="flex w-full flex-1 flex-col lg:flex-row lg:items-start">
      <CheckoutWizardRail
        currentStep={currentStep}
        maxReachableStep={maxReachableStep}
        onGoToStep={goToStep}
      />

      <div className="flex flex-1 flex-col min-w-0">
        <CheckoutMobileProgress currentStep={currentStep} />

        <div className="flex w-full flex-col gap-8 px-4 pt-8 pb-[calc(var(--checkout-action-bar-h,5.5rem)+1.5rem)] sm:px-6 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(18rem,30%)] lg:gap-x-10 lg:gap-y-8 lg:px-8 lg:pt-10">
          <div className="min-w-0 lg:col-span-2 animate-in fade-in duration-300">
            {currentStep === 0 ? (
              <CheckoutStepIntro
                ref={(el) => (stepHeadingRefs.current[0] = el)}
                title="Confirme seu pacote"
                description="Revise nome, conteúdo e valor antes de seguir para os seus dados."
              />
            ) : null}
            {currentStep === 1 ? (
              <CheckoutStepIntro
                ref={(el) => (stepHeadingRefs.current[1] = el)}
                title="Seus dados"
                description="Informe e-mail e telefone para liberar o acesso e enviar a confirmação da compra."
              />
            ) : null}
            {currentStep === 2 ? (
              <CheckoutStepIntro
                ref={(el) => (stepHeadingRefs.current[2] = el)}
                title="Pagamento"
                description={
                  isInstrumentStage
                    ? getPaymentInstrumentIntro(state.selectedPaymentKey)
                    : "Escolha como pagar. Confirme na barra para iniciar o método selecionado."
                }
              />
            ) : null}
          </div>

          <div className="flex min-w-0 flex-col gap-8 lg:row-start-2 animate-in fade-in duration-300" key={currentStep}>
            {currentStep === 0 ? (
              <CheckoutPackageStep showIntro={false} />
            ) : null}

            {currentStep === 1 ? <CustomerInformation /> : null}

            {currentStep === 2 ? (
              <>
                <CheckoutPaymentMobileRecap />
                <div className="lg:hidden">
                  <PromotionalCode variant="compact" />
                </div>
                {isInstrumentStage && paymentSelection && state.paymentSession ? (
                  <>
                    <CheckoutPaymentMethodRecap
                      selectedPaymentKey={state.selectedPaymentKey}
                      onChangeMethod={handleChangePaymentMethod}
                    />
                    <PaymentAdapterRenderer
                      provider={paymentSelection.provider}
                      method={paymentSelection.method}
                      session={state.paymentSession}
                      orderId={state.orderId || ""}
                      amount={getFinalPrice()}
                      hideTitle
                      onCardSubmit={handleCardSubmit}
                    />
                    {isCardMethod && cardPaymentSubmitted && !timedOut ? (
                      <PaymentProcessingOverlay
                        phase={overlayPhase}
                        orderId={state.orderId}
                        packageId={packageInfo?.id}
                        packageName={packageInfo?.name}
                      />
                    ) : null}
                    {timedOut ? (
                      <div
                        id="checkout-awaiting-confirmation"
                        className="scroll-mb-[calc(var(--checkout-action-bar-h,5.5rem)+1rem)] rounded-md border border-success bg-success-muted p-4 text-sm text-success-subtle"
                        aria-live="polite"
                      >
                        <div className="space-y-3">
                          <p>{PAYMENT_AWAITING_HINT}</p>
                          <Button
                            type="button"
                            onClick={() => refetch()}
                            className={CHECKOUT_PRIMARY_CTA_CLASS}
                          >
                            {PAYMENT_RETRY_BUTTON_LABEL}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <>
                    <PaymentMethodSelection
                      selectedPaymentKey={state.selectedPaymentKey}
                      setPaymentMethod={setPaymentMethod}
                      disabled={state.isCommittingOrder}
                    />
                    <div className="rounded-lg border border bg-surface p-4 text-sm leading-relaxed text-surface-foreground">
                      {getPaymentPlaceholderHint(state.selectedPaymentKey)}
                    </div>
                  </>
                )}
              </>
            ) : null}
          </div>

          <aside className="hidden min-w-0 lg:block lg:row-start-2 lg:self-start">
            <OrderSummary currentStep={currentStep} />
          </aside>
        </div>
      </div>

      <CheckoutActionBar
        finalPriceLabel={formatCurrency(getFinalPrice())}
        primaryLabel={primaryLabel}
        primaryDisabled={primaryDisabled}
        primaryLoading={isCommitting}
        waiting={isAwaitingPayment}
        showBack={showActionBack}
        onBack={goBack}
        onPrimary={() => void handlePrimaryAction()}
      />
    </div>
  );
};

export const CheckoutPage: React.FC = () => {
  const params = useParams<{ packageId?: string }>();
  const [searchParams] = useSearchParams();
  const packageId = params.packageId;
  const voucherCode = searchParams.get("voucher") || undefined;

  return (
    <CheckoutProvider packageId={packageId} voucherCode={voucherCode}>
      <CheckoutPageWrapper />
    </CheckoutProvider>
  );
};

const CheckoutPageWrapper: React.FC = () => {
  const { isLoading, state, packageInfo, packageId } = useCheckoutContext();
  const navigate = useNavigate();
  const [unavailableDialogOpen, setUnavailableDialogOpen] = React.useState(false);
  const alreadyOwnedRedirectedRef = React.useRef(false);

  React.useEffect(() => {
    if (!state.isPackageAlreadyPurchased || !packageId) return;
    if (alreadyOwnedRedirectedRef.current) return;
    alreadyOwnedRedirectedRef.current = true;

    navigate(`/student/packages?alreadyOwned=${encodeURIComponent(packageId)}`, {
      replace: true,
      state: packageInfo?.name ? { name: packageInfo.name } : {},
    });
  }, [state.isPackageAlreadyPurchased, packageId, packageInfo?.name, navigate]);

  React.useEffect(() => {
    if (state.isPackageUnavailableForPurchase) {
      setUnavailableDialogOpen(true);
    }
  }, [state.isPackageUnavailableForPurchase]);

  const handleUnavailableDialogClose = () => {
    setUnavailableDialogOpen(false);
    navigate("/student/packages");
  };

  if (state.isInitializingOrder || state.isPackageAlreadyPurchased) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/90">
        <div className="flex flex-col items-center space-y-4">
          <LoadingSpinner size="lg" className="text-success" />
          <p className="text-text-secondary text-lg">
            {state.isPackageAlreadyPurchased
              ? "Redirecionando para Meus pacotes..."
              : "Preparando seu pedido..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div id="ofcourse-checkout" className="min-h-screen bg-surface flex flex-col">
        <header id="header" className="bg-background border-b border lg:hidden">
          <div className="container mx-auto px-4 py-4 flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate("/student/packages")}
              className="flex items-center gap-1 text-sm text-text-secondary hover:text-foreground transition-colors"
              aria-label="Voltar para pacotes"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Pacotes</span>
            </button>
          </div>
        </header>

        <header className="hidden lg:block h-[4.5rem] shrink-0 border-b border bg-background">
          <div className="flex h-full w-full items-center gap-4 px-6 lg:px-8">
            <button
              type="button"
              onClick={() => navigate("/student/packages")}
              className="flex items-center gap-1 text-sm text-text-secondary hover:text-foreground transition-colors"
              aria-label="Voltar para pacotes"
            >
              <ArrowLeft className="w-4 h-4" />
              Pacotes
            </button>
            {packageInfo?.name ? (
              <p className="ml-auto text-sm text-text-tertiary truncate max-w-[280px]">{packageInfo.name}</p>
            ) : null}
          </div>
        </header>

        <main className="flex flex-grow w-full flex-col min-h-0">
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="text-text-secondary">Carregando pacote...</div>
            </div>
          ) : (
            <CheckoutPageContent />
          )}
        </main>
      </div>

      <Dialog open={unavailableDialogOpen} onOpenChange={handleUnavailableDialogClose}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader className="space-y-6 pb-2">
            <div className="flex flex-col items-center space-y-4">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-surface-subtle border-2 border">
                <Info className="w-7 h-7 text-surface-foreground" />
              </div>
              <DialogTitle className="text-xl font-semibold text-foreground text-center">
                Pacote indisponível para compra
              </DialogTitle>
            </div>
            <DialogDescription className="text-center space-y-3 pt-2">
              <p className="text-surface-foreground text-base leading-relaxed">
                Este pacote não está à venda agora. A promoção pode ter expirado ou ainda não ter
                começado.
              </p>
              <p className="text-text-tertiary text-sm leading-relaxed">
                Volte à lista de pacotes para ver o que está disponível.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-6 pb-2">
            <Button
              onClick={handleUnavailableDialogClose}
              className={`w-full font-medium py-2.5 ${CHECKOUT_PRIMARY_CTA_CLASS}`}
            >
              Ver pacotes disponíveis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
